// TokenModal.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
    Dialog,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import { list } from "../contexts/SelectTokenDATA";
import { getCurrencyMetadata, balanceOf } from "thirdweb/extensions/erc20";
import { getErc20Contract } from "../contexts/thirdWebClient";
import {
    useActiveAccount,
    useActiveWalletChain,
} from "thirdweb/react";
import { toast } from "react-toastify";
import { ethers } from "ethers";

const TokenModal = ({ open, setOpen, onClick, excludeToken }) => {
    const tokens = list.filter(t => !excludeToken || t.address !== excludeToken.address);
    const [contractAddress, setContractAddress] = useState("");
    const [tokenDetals, setToknDetails] = useState({});
    const [balances, setBalances] = useState({});

    const add = useActiveAccount();
    const [userAddress, setAddress] = useState("");

    const chain = useActiveWalletChain();
    useEffect(() => {
        if (add && add.address) {
            setAddress(add.address);
        }
    }, [add, chain]);

    // Fetch balances for all tokens when modal opens
    const fetchBalances = useCallback(async () => {
        if (!userAddress || !chain?.id) return;
        const newBalances = {};
        for (const token of tokens) {
            try {
                const contract = getErc20Contract(token.address, chain);
                const bal = await balanceOf({ contract, address: userAddress });
                newBalances[token.address.toLowerCase()] = ethers.formatUnits(bal, token.decimals || 18);
            } catch {
                newBalances[token.address.toLowerCase()] = "0";
            }
        }
        setBalances(newBalances);
    }, [userAddress, chain, tokens]);

    useEffect(() => {
        if (open && userAddress) {
            fetchBalances();
        }
    }, [open, userAddress, fetchBalances]);

    // Clear search when modal closes
    useEffect(() => {
        if (!open) {
            setContractAddress("");
            setToknDetails({});
        }
    }, [open]);

    const isValidAddress = (addr) => /^0x[a-fA-F0-9]{40}$/.test(addr);

    const tokenDetailsLookup = async (value) => {
        try {
            if (value && isValidAddress(value) && chain && chain?.id) {
                const contract = getErc20Contract(value, chain);
                let currencyMetadata = await getCurrencyMetadata({ contract });
                currencyMetadata.address = value;
                setToknDetails(currencyMetadata);
            } else {
                setToknDetails({});
            }
        } catch (error) {
            toast.error("Invalid token address or token not found on this network");
            console.log("%c Line:53 🍎 error", "color:#6ec1c2", error);
        }
    };

    const selectToken = () => {
        if (excludeToken && tokenDetals?.address?.toLowerCase() === excludeToken?.address?.toLowerCase()) {
            toast.error("Cannot select the same token for both fields");
            return;
        }
        onClick(tokenDetals);
        setOpen(false);
        setContractAddress("");
        setToknDetails({});
    };

    const selectNativeToken = (data) => {
        onClick(data);
        setOpen(false);
        setContractAddress("");
        setToknDetails({});
    };

    // Filter tokens by name or symbol
    const searchTerm = contractAddress.trim().toLowerCase();
    const isAddressSearch = isValidAddress(contractAddress.trim());
    const filteredTokens = searchTerm && !isAddressSearch
        ? tokens.filter(t =>
            t.name.toLowerCase().includes(searchTerm) ||
            t.symbol.toLowerCase().includes(searchTerm)
        )
        : tokens;

    const formatBalance = (bal) => {
        if (!bal || bal === "0") return "0";
        const num = parseFloat(bal);
        if (num === 0) return "0";
        if (num < 0.0001) return "<0.0001";
        if (num >= 1000000) return (num / 1000000).toFixed(2) + "M";
        if (num >= 1000) return (num / 1000).toFixed(2) + "K";
        return num.toFixed(4);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setContractAddress(value);
        // Only do address lookup if it's a valid address
        if (isValidAddress(value.trim())) {
            tokenDetailsLookup(value.trim());
        } else {
            setToknDetails({});
        }
    };

    // Show address search result
    const showAddressResult = isAddressSearch && tokenDetals && tokenDetals?.address;

    return (
        <Dialog open={open} onClose={() => setOpen(!open)} maxWidth="sm" fullWidth
            PaperProps={{
                className: "card-3d !rounded-[24px]",
                style: { background: 'none', boxShadow: 'none' }
            }}
        >
            <div className="card-3d p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                    <h2 className="text-[20px] font-bold gradient-text">Select Token</h2>
                    <button
                        onClick={() => setOpen(!open)}
                        className="swap-arrow-3d w-9 h-9 flex items-center justify-center"
                    >
                        <IoClose className="text-dexora-text-secondary size-[18px]" />
                    </button>
                </div>

                {/* Search Input */}
                <div className="inner-3d mb-4">
                    <input
                        value={contractAddress}
                        onChange={handleSearchChange}
                        id="search"
                        placeholder="Search name or paste address"
                        type="text"
                        className="w-full py-3 px-4 bg-transparent text-dexora-text-primary placeholder:text-dexora-text-muted/50 outline-none text-[14px] font-medium rounded-2xl"
                    />
                </div>

                {/* Token List */}
                {showAddressResult ? (
                    <div>
                        <p className="text-[12px] font-semibold text-dexora-text-muted uppercase tracking-widest mb-3">Search Result</p>
                        <div
                            onClick={() => selectToken()}
                            className="token-pill-3d flex items-center gap-3 px-4 py-3 cursor-pointer w-full"
                        >
                            <div className="w-8 h-8 rounded-full bg-dexora-cyan/20 flex items-center justify-center">
                                <span className="text-dexora-cyan text-[12px] font-bold">{tokenDetals?.symbol?.charAt(0)}</span>
                            </div>
                            <div className="flex-1">
                                <p className="text-dexora-text-primary text-[15px] font-bold">{tokenDetals?.name}</p>
                                <p className="text-dexora-text-muted text-[12px] font-medium">{tokenDetals?.symbol}</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <p className="text-[12px] font-semibold text-dexora-text-muted uppercase tracking-widest mb-3">
                            {searchTerm ? "Search Results" : "Popular Tokens"}
                        </p>
                        <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto">
                            {filteredTokens.length > 0 ? (
                                filteredTokens.map((token, index) => {
                                    const bal = balances[token.address.toLowerCase()];
                                    return (
                                        <div
                                            onClick={() => {
                                                selectNativeToken(token);
                                                setOpen(false);
                                            }}
                                            key={index}
                                            className="token-pill-3d flex items-center gap-3 px-4 py-3 cursor-pointer w-full"
                                        >
                                            {token?.img ? (
                                                <img
                                                    src={token?.img}
                                                    alt={token?.name}
                                                    className="size-[32px] rounded-full ring-1 ring-white/10"
                                                />
                                            ) : (
                                                <div className="w-8 h-8 rounded-full bg-dexora-cyan/20 flex items-center justify-center">
                                                    <span className="text-dexora-cyan text-[12px] font-bold">{token?.symbol?.charAt(0)}</span>
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-dexora-text-primary text-[15px] font-bold">{token?.name}</p>
                                                <p className="text-dexora-text-muted text-[12px] font-medium">{token?.symbol}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-dexora-text-primary text-[14px] font-semibold">
                                                    {bal !== undefined ? formatBalance(bal) : "—"}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-dexora-text-muted text-[13px] text-center py-4">No tokens found</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default TokenModal;
